import clsx from 'clsx'
import {
	Button,
	Icon,
	ImageCropSettings,
	initialStateModal,
	Input,
	ModalConfirm,
	Typography
} from 'components'
import { useDebounceEffect, useToast } from 'hooks'
import { Icons, SlideImageFormInputs, TypographyTypes } from 'interfaces'
import React, { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import {
	centerCrop,
	convertToPixelCrop,
	Crop,
	makeAspectCrop,
	PixelCrop,
	ReactCrop
} from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'

import { CircularProgress } from '@mui/material'
import { getSignatureImageCardComponent } from 'api'
import Cookies from 'universal-cookie'
import { canvasPreview, generateTransform, regexVimeoOrYoutube } from 'utils'
import { ImageCropProps } from './SlideImageContentProps.type'
import {
	handleFinishCrop,
	handleUploadImage,
	updatePhoto
} from './utilFuntions'

export const ImageCrop: React.FC<ImageCropProps> = ({
	isFinishCropButtonVisible,
	isCropSettingsVisible,
	sendImageCropped,
	listImage,
	setOpenSlide,
	watch,
	setSlideImages,
	setSlideLibraryImages,
	setIsCropSettingsVisible,
	setSendImageCropped,
	setIsFinishCropButtonVisible,
	onSubmited,
	setValue: setValueInput,
	setListImage,
	remove
}): JSX.Element => {
	const { showToast } = useToast()
	const [disableMultiple, setDisableMultiple] = useState(false)
	const previewCanvasRef = useRef<HTMLCanvasElement | null>(null)
	const imgRef = useRef<HTMLImageElement>(null)
	const [crop, setCrop] = useState<Crop | null>()
	const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>()
	const [scale, setScale] = useState(1)
	const [rotate, setRotate] = useState(0)
	const [flipVertical, setFlipVertical] = useState(false)
	const [flipHorizontal, setFlipHorizontal] = useState(false)
	const [aspectString, setAspectString] = useState<string | undefined>()
	const [isActiveFinish, setIsActiveFinish] = useState(false)
	const [isCropped, setIsCropped] = useState(true)
	const [loading, setLoading] = useState(false)
	const cookies = new Cookies()
	const organizationId = cookies.get('organization_id')
	const arrayImage = watch().assets.map(elemet => elemet.title)

	const [modalProps, setModalProps] = useState(initialStateModal)

	const {
		register,
		setValue,
		watch: watchInput,
		handleSubmit,
		formState: { errors, isValid }
	} = useForm<SlideImageFormInputs>({
		mode: 'onChange',
		defaultValues: {
			url: watch().video || ''
		}
	})
	const linkVideo = watchInput('url').trim()

	const rules = {
		url: {
			maxLength: { value: 150, message: 'Maximum characters exceeded' },
			pattern: {
				value: regexVimeoOrYoutube,
				message: 'Invalid link'
			}
		}
	}

	const isAssetCropped =
		listImage[listImage.length - 1]?.asset_url?.includes('.amazonaws.com/')

	useEffect(() => {
		if (listImage.length >= 5) {
			setDisableMultiple(true)
		} else {
			setDisableMultiple(false)
		}
	}, [listImage])

	const resetCrop = (): void => {
		setCrop(null)
		setCompletedCrop(null)
	}

	const resetCropState = (): void => {
		resetCrop()
		setIsCropSettingsVisible?.(true)
		setIsFinishCropButtonVisible(true)
		setScale(1)
		setRotate(0)
		setFlipVertical(false)
		setFlipHorizontal(false)
		setIsActiveFinish(false)
	}

	useDebounceEffect(
		() => {
			const generateCanvasPreview = async (): Promise<void> => {
				if (
					completedCrop?.width &&
					completedCrop?.height &&
					imgRef.current &&
					previewCanvasRef.current
				) {
					await canvasPreview(
						imgRef.current,
						previewCanvasRef.current,
						completedCrop,
						scale,
						rotate
					)
				}
			}

			generateCanvasPreview()
		},
		500,
		[completedCrop, scale, rotate, flipVertical, flipHorizontal]
	)

	const handleDeleteImage = (id: number): void => {
		const newListImage = listImage
		newListImage.splice(id, 1)
		setListImage([...newListImage])
		setSendImageCropped([...newListImage])
		setIsCropped(true)
		setAspectString(undefined)
		resetCropState()
		const input = document.getElementById('image') as HTMLInputElement
		if (input) {
			input.value = ''
		}
	}

	const onSubmit: SubmitHandler<SlideImageFormInputs> = async () => {
		setValueInput('video', linkVideo)
		setLoading(true)
		if (sendImageCropped && sendImageCropped.length > 0) {
			setLoading(true)
			try {
				const credentials = await getSignatureImageCardComponent(organizationId)
				await Promise.all(
					sendImageCropped.map(async elemet => {
						if (
							elemet.asset_url &&
							!elemet.asset_url.includes('.amazonaws.com/')
						) {
							try {
								if (elemet.asset_name) {
									const imageCard = await updatePhoto(
										elemet.asset_url,
										credentials,
										`size=${elemet.asset_aspect}-${elemet.asset_name}`
									)

									onSubmited({ title: imageCard })
								}
							} catch (error) {
								console.error('Error uploading image:', error)
							}
						} else if (!arrayImage.includes(elemet.asset_url)) {
							onSubmited({ title: elemet.asset_url })
						}
					})
				)
			} catch (error) {
				console.error('Error during image upload process:', error)
			} finally {
				setLoading(false)
				setOpenSlide(false)
				setSlideImages(false)
			}
		} else {
			setLoading(false)
			setOpenSlide(false)
			setSlideImages(false)
		}
	}

	const centerAspectCrop = (
		mediaWidth: number,
		mediaHeight: number,
		aspectt: number
	): Crop => {
		return centerCrop(
			makeAspectCrop(
				{
					unit: '%',
					width: 90
				},
				aspectt,
				mediaWidth,
				mediaHeight
			),
			mediaWidth,
			mediaHeight
		)
	}

	const handleToggleAspectClick = (
		aspect: number,
		aspectStringg: string
	): void => {
		setAspectString(aspectStringg)
		if (imgRef.current) {
			const { width, height } = imgRef.current
			const newCrop = centerAspectCrop(width, height, aspect)
			setCrop(newCrop)
			setCompletedCrop(convertToPixelCrop(newCrop, width, height))
		}
	}

	const handleControlZoom = (): void => {
		if (completedCrop) {
			if (scale <= 0.5) {
				setScale(0.5)
			}
		}
	}

	const confirmDeleteImage = (assetUrl?: string, index?: number): void => {
		setModalProps({
			open: true,
			title: 'Image will be deleted!',
			description: 'This image will be deleted. Do you want to continue?',
			onConfirm: () => {
				if (assetUrl && index !== undefined) {
					if (arrayImage.includes(assetUrl)) {
						const indexArray = arrayImage.indexOf(assetUrl)
						remove(indexArray)
					}
					const newListImage = listImage
					newListImage.splice(index, 1)
					setListImage([...newListImage])
					setSendImageCropped([...newListImage])
				} else {
					const item = listImage[listImage.length - 1]
					if (arrayImage.includes(item.asset_url)) {
						const indexArray = arrayImage.indexOf(item.asset_url)
						remove(indexArray)
					}
					handleDeleteImage(-1)
				}
				setModalProps(initialStateModal)
			}
		})
	}

	if (loading)
		return (
			<div className="flex h-full w-full justify-center items-center">
				<CircularProgress disableShrink />
			</div>
		)

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className="h-full flex flex-col justify-between"
		>
			<div>
				{listImage.length === 0 ? (
					<div className="bg-gray-11 px-[61px] py-36 w-full my-4">
						<div className="flex items-center justify-center gap-2 py-3 px-5 bg-white shadow-boxPreview rounded w-full">
							<Typography
								fontLeading="15/18"
								type={TypographyTypes.p}
								className="!font-normal text-lavender"
								title="You can use your own or grab one from the Library. 
						Your Images and Videos will appear here as you add them."
							/>
							<Icon src={Icons.uploadCloud} className="min-w-[25px] w-[25px]" />
						</div>
					</div>
				) : (
					<div className="my-4 relative">
						<div className="relative">
							<div className="flex justify-center items-center">
								<ReactCrop
									crop={crop as Crop}
									onChange={(_, percentCrop) => {
										setCrop(percentCrop)
										setIsActiveFinish(false)
									}}
									onComplete={c => {
										setCompletedCrop(c)
										setIsActiveFinish(true)
									}}
									locked
								>
									<img
										src={listImage[listImage.length - 1].asset_url}
										alt="preview content"
										className="!max-h-[20rem]"
										style={{
											transform: generateTransform(
												scale,
												rotate,
												flipHorizontal,
												flipVertical
											)
										}}
										ref={imgRef}
									/>
								</ReactCrop>

								{listImage.length > 1 && (
									<div
										className={`flex gap-2 bg-gray-9 rounded-lg p-3 max-w-max absolute ${
											isCropSettingsVisible && !isAssetCropped
												? 'bottom-[18.5rem]'
												: 'bottom-[1.5rem]'
										} left-[1rem]`}
									>
										{listImage.map(
											(item, index) =>
												listImage.length - 1 !== index && (
													<div
														className="relative"
														key={`${item.asset_aspect}-${index}`}
													>
														<img
															src={item.asset_url}
															alt="preview content"
															className="w-[59px] h-14 rounded-lg"
														/>
														<button
															type="button"
															onClick={() => {
																confirmDeleteImage(item.asset_url, index)
															}}
															className="absolute -top-1 -right-1 bg-gray-18 p-1 rounded-full border-2 border-gray-3"
														>
															<Icon src={Icons.close} className="w-3 h-3 " />
														</button>
													</div>
												)
										)}
									</div>
								)}
							</div>
							{isCropSettingsVisible && !isAssetCropped && (
								<ImageCropSettings
									handleToggleAspectClick={handleToggleAspectClick}
									rotate={rotate}
									setRotate={setRotate}
									scale={scale}
									setScale={setScale}
									handleControlZoom={handleControlZoom}
									completedCrop={completedCrop as PixelCrop}
									aspectString={aspectString}
								/>
							)}
							{isFinishCropButtonVisible && !isAssetCropped && (
								<div className="pt-2">
									<Button
										label="Finish Crop"
										type="button"
										color="Primary"
										className="w-full mt-1"
										onClick={() => {
											const resultImage = handleFinishCrop(
												previewCanvasRef,
												aspectString,
												listImage,
												completedCrop
											)
											setSendImageCropped(prevList => [
												...prevList,
												resultImage
											])
											setIsCropped(true)
											setRotate(0)
											setScale(1)
											setIsFinishCropButtonVisible(false)
											setIsCropSettingsVisible?.(false)
											resetCrop()
										}}
										disabled={!isActiveFinish}
									/>
								</div>
							)}
							{!!completedCrop && (
								<canvas
									id="croppedCanvas"
									className=" h-0 w-0 rounded-lg border-blue-300"
									ref={previewCanvasRef}
									style={{
										visibility: 'hidden',
										border: '1px solid black',
										objectFit: 'contain'
									}}
								/>
							)}
							<button
								type="button"
								onClick={() => {
									confirmDeleteImage()
								}}
								className="absolute -top-1 -right-1 bg-gray-18 p-1 rounded-full border-2 border-gray-3"
							>
								<Icon src={Icons.close} className="w-3 h-3 " />
							</button>
						</div>
					</div>
				)}
				<input
					id="image"
					type="file"
					className="hidden"
					onChange={event => {
						if (event.target.files && event.target.files.length !== 0) {
							setIsCropped(false)
							handleUploadImage(
								event.target.files[0],
								setListImage,
								setAspectString,
								setIsCropped,
								resetCropState,
								message => {
									showToast('Error', message, 'error')
								}
							)
						} else {
							setIsCropped(true)
						}
					}}
					accept="image/png, image/jpeg, image/jpg"
				/>
				<div className="flex flex-col gap-4">
					<Button
						color="Primary"
						label="Upload From Your Device"
						className="w-full mt-1"
						onClick={() => {
							const input = document.getElementById('image')
							if (input) input.click()
						}}
						disabled={listImage.length >= 5 || !isCropped}
					/>
					<Button
						color="Transparent"
						iconLeft={Icons.addImages}
						className="w-full mt-1"
						label="Browse From Your Library"
						onClick={() => {
							setSlideImages(false)
							setSlideLibraryImages(true)
						}}
						disabled={listImage.length >= 5 || !isCropped}
					/>
					<button
						type="button"
						className="flex items-center justify-center gap-3 w-full"
						onClick={() => {
							const input = document.getElementById('image')
							if (input) input.click()
						}}
						disabled={listImage.length >= 5 || !isCropped}
					>
						<Icon
							src={Icons.plus}
							className={clsx(
								'w-5 h-5',
								disableMultiple || !isCropped
									? 'text-gray-9'
									: 'text-blue-primary '
							)}
							fillPath
						/>
						<Typography
							title="Add Multiple Images"
							className={clsx(
								'!font-semibold',
								disableMultiple || !isCropped
									? 'text-gray-9'
									: 'text-blue-primary '
							)}
							type={TypographyTypes.span}
							fontLeading="15/22"
						/>
					</button>
				</div>
				<div className="pt-4">
					<Input
						name="url"
						title="Link to Your Video"
						register={register}
						rules={rules.url}
						error={errors.url}
						borderFull={false}
						setValueInput={setValue}
						inputClassName="rounded"
						placeholder="YouTube or Vimeo"
					/>
				</div>
			</div>
			<div className="flex items-center justify-between mt-4">
				<Button
					type="button"
					label="Cancel"
					color="Transparent"
					className="!min-w-[130px]"
					onClick={() => {
						setOpenSlide(false)
						setSlideImages(false)
					}}
				/>
				<Button
					label="Save"
					type="submit"
					color="Primary"
					className="!min-w-[130px]"
					disabled={
						(listImage.length === 0 && !linkVideo) || !isCropped || !isValid
					}
				/>
			</div>

			<ModalConfirm
				open={modalProps.open}
				setOpen={() => setModalProps(initialStateModal)}
				hasCancel
				buttonLabelCancel="Cancel"
				buttonLabel="Yes"
				flexButton="row"
				handleCancel={() => setModalProps(initialStateModal)}
				onClickConfirm={modalProps.onConfirm}
				icon={Icons.warningActive}
				title={modalProps.title}
				descripcion={modalProps.description}
				classNameButton="h-[32px] w-[151px] m-0 !py-0"
			/>
		</form>
	)
}
